'use client';

import { useContext, useEffect, useMemo, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { Watchlist } from '@benzinga/watchlist-manager';

import { getDelayedHoldingQuotes } from '../data';
import {
  aggregateHoldingsBySymbol,
  hasLinkedHoldings,
  PricedHolding,
  priceHoldings,
} from './portfolioActionUtils';

export const useHoldingPrices = (watchlists: Pick<Watchlist, 'symbols'>[]) => {
  const session = useContext(SessionContext);
  const [pricesBySymbol, setPricesBySymbol] = useState<Record<string, number>>({});
  const [companyNamesBySymbol, setCompanyNamesBySymbol] = useState<Record<string, string>>({});
  const [sectorBySymbol, setSectorBySymbol] = useState<Record<string, string>>({});
  const [loadedSymbolsKey, setLoadedSymbolsKey] = useState<string | null>(null);
  const [isFetchInFlight, setIsFetchInFlight] = useState(false);

  const hasHoldings = useMemo(() => hasLinkedHoldings(watchlists), [watchlists]);
  const aggregatedHoldings = useMemo(() => aggregateHoldingsBySymbol(watchlists), [watchlists]);
  const holdingSymbolsKey = useMemo(
    () => aggregatedHoldings.map(holding => holding.symbol).join(','),
    [aggregatedHoldings],
  );

  const isLoadingPrices = Boolean(hasHoldings && holdingSymbolsKey) && (isFetchInFlight || loadedSymbolsKey !== holdingSymbolsKey);
  const isLoadingSectors = isLoadingPrices;

  useEffect(() => {
    if (!hasHoldings || !holdingSymbolsKey) {
      setPricesBySymbol({});
      setCompanyNamesBySymbol({});
      setSectorBySymbol({});
      setLoadedSymbolsKey(null);
      setIsFetchInFlight(false);
      return;
    }

    const symbols = holdingSymbolsKey.split(',');
    const abortController = new AbortController();

    const loadHoldingsData = async () => {
      setIsFetchInFlight(true);

      try {
        const holdingsData = await getDelayedHoldingQuotes(symbols, session.getSession());
        if (abortController.signal.aborted) return;
        setPricesBySymbol(holdingsData.pricesBySymbol);
        setCompanyNamesBySymbol(holdingsData.companyNamesBySymbol);
        setSectorBySymbol(holdingsData.allocationBySymbol);
        setLoadedSymbolsKey(holdingSymbolsKey);
      } catch {
        if (abortController.signal.aborted) return;
        setLoadedSymbolsKey(holdingSymbolsKey);
      } finally {
        if (!abortController.signal.aborted) {
          setIsFetchInFlight(false);
        }
      }
    };

    loadHoldingsData();

    return () => {
      abortController.abort();
    };
  }, [hasHoldings, holdingSymbolsKey, session]);

  const pricedHoldings = useMemo(
    (): PricedHolding[] => priceHoldings(aggregatedHoldings, pricesBySymbol, companyNamesBySymbol),
    [aggregatedHoldings, companyNamesBySymbol, pricesBySymbol],
  );

  return {
    hasHoldings,
    holdingSymbolsKey,
    isLoadingPrices,
    isLoadingSectors,
    pricedHoldings,
    sectorBySymbol,
  };
};
